import { FC, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FileDropzone, Input, Modal, Select } from '@forma/forma-ui-kit';
import SelectSideModal from 'components/SelectSideModal';
import getValidationSchema from 'data/validationSchema';
import SelectOwnerModal from '../SelectOwnerModal';

import { ISidesListEditData, ITemplateSide } from 'interfaces/templates.interface';

import styles from './send-sign-modal.module.css';
import { useAppDispatch } from 'store/hooks';
import { addNotification } from 'store/notifications/notificationsSlice';

interface SendSignModalProps {
  open: boolean,
  onClose: () => void,
  onSubmit: (ownerId: string, name: string, signer: { name: string, phone: string }, files: File[]) => void,
  isLoading?: boolean,
  name: string,
  sides?: ITemplateSide[],
  sidesData?: ISidesListEditData
}

interface FormValues {
  documentName: string,
  fullName: string,
  phone: string
}

const phoneVarsIds = [
  '1204',
  '4008',
  '4009',
  '4273',
  '4276'
];

const fullNameVarsIds = [
  ['101', '102', '103'],
  ['318', '319', '320'],
  ['1008', '1003', ''],
  ['2043', '2044', '2045'],
  ['3024', '3025', '3026'],
  ['3445', '3446', '3447'],
  ['3498', '3499', '3500'],
  ['3906', '3907', '3908'],
  ['4259', '4260', '4261'],
  ['4262', '4263', '4264'],
];

const SendSignModal: FC<SendSignModalProps> = ({ open, onClose, onSubmit, isLoading, name, sides, sidesData }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [selectedSideId, setSelectedSideId] = useState<string | null>(null);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  const { register, control, handleSubmit, setValue, formState: { isValid, errors } } = useForm<FormValues>({
    resolver: yupResolver(getValidationSchema(['documentName', 'fullName', 'phone'])),
    defaultValues: { documentName: name }
  });

  const sideValues = useMemo(() => sidesData && selectedSideId && sidesData[selectedSideId], [sidesData, selectedSideId]);
  const namesOptions = useMemo(() => sideValues ? fullNameVarsIds.reduce((acc: { label: string, value: string }[], ids) => {
    const value = ids.reduce((acc, id) => {
      if (!sideValues.tattrValues[id]) return acc;
      if (!acc) return sideValues.tattrValues[id];
      return acc + ' ' + sideValues.tattrValues[id];
    }, '');
    return value ? [...acc, { label: value, value }] : acc;
  }, []) : undefined, [sideValues]);
  const phonesOptions = useMemo(() => sideValues ? phoneVarsIds.reduce((acc: { label: string, value: string }[], id) => {
    const value = sideValues.tattrValues[id];
    return value ? [...acc, { label: value, value }] : acc;
  }, []) : undefined, [sideValues]);

  useEffect(() => {
    if (namesOptions?.length === 1) setValue('fullName', namesOptions[0].value);
    // eslint-disable-next-line
  }, [namesOptions]);

  useEffect(() => {
    if (phonesOptions?.length === 1) setValue('phone', phonesOptions[0].value);
    // eslint-disable-next-line
  }, [phonesOptions]);

  useEffect(() => {
    if (sideValues && !namesOptions?.length && !phonesOptions?.length) {
      dispatch(addNotification({ content: t('errors.phone_name_not_compared'), type: 'ERROR' }));
    }
    // eslint-disable-next-line
  }, [sideValues, namesOptions, phonesOptions]);

  const handleSubmitForm = (values: FormValues) => {
    if (!selectedOwnerId) return;

    onSubmit(
      selectedOwnerId,
      values.documentName,
      {
        name: values.fullName,
        phone: values.phone
      },
      files
    );
  };

  if (!selectedOwnerId) return (
    <SelectOwnerModal
      open={open && !selectedOwnerId}
      onClose={onClose}
      onSubmit={setSelectedOwnerId}
      isSign
    />
  );

  if (sides && !selectedSideId) return (
    <SelectSideModal
      open={open && !selectedSideId}
      onClose={onClose}
      sides={sides}
      onSelectSide={setSelectedSideId}
      title={t('select_side_for_sign_edo')}
    />
  );

  return (
    <Modal
      title={t('send_to_counteragent_sign')}
      open={open}
      onClose={onClose}
      size="large"
      buttons={[
        {
          type: 'submit',
          children: t('send_to_sign'),
          className: styles.save,
          isLoading: isLoading,
          disabled: !selectedOwnerId || !isValid,
          form: 'send_sign_form',
          closing: false
        }
      ]}
    >
      <div className={styles.description}>{t('send_to_counteragent_sign_description')}</div>
      <div className={styles.content}>
        <form id="send_sign_form" className={styles.form} onSubmit={handleSubmit(handleSubmitForm)}>
          <Controller
            control={control}
            name="documentName"
            render={({ field: { value = '', ...rest } }) => (
              <Input
                placeholder={t('enter_document_name')}
                viewStyle="secondary"
                maxLength={255}
                {...rest}
                {...register}
                value={value}
                meta={{
                  containerClass: styles.field,
                  label: t('document_name'),
                  error: !!errors?.documentName?.message,
                  errorMessage: errors?.documentName?.message && t('errors.' + errors.documentName.message),
                  showClearButton: true
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="fullName"
            render={({ field: { value = '', ...rest } }) => (
              <Select
                placeholder={t('placeholders.name')}
                viewStyle="secondary"
                options={namesOptions}
                // maxLength={255}
                {...rest}
                {...register}
                value={value}
                meta={{
                  dropdownClassName: styles.dropdown,
                  containerClass: styles.field,
                  label: t('fullname_short'),
                  error: !!errors?.fullName?.message,
                  errorMessage: errors?.fullName?.message && t('errors.' + errors.fullName.message),
                  creatable: true
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="phone"
            render={({ field: { value = '', ...rest } }) => (
              <Select
                placeholder={t('placeholders.phone')}
                viewStyle="secondary"
                options={phonesOptions}
                {...rest}
                {...register}
                value={value}
                meta={{
                  dropdownClassName: styles.dropdown,
                  containerClass: styles.field,
                  label: t('fields.phone'),
                  error: !!errors?.phone?.message,
                  errorMessage: errors?.phone?.message && t('errors.' + errors.phone.message),
                  creatable: true
                }}
              />
              // <PhoneInput
              //   masks={phonemasks}
              //   defaultMaskCode={(i18n.resolvedLanguage === 'en' || !i18n.resolvedLanguage) ? 'US' : i18n.resolvedLanguage.toUpperCase()}
              //   containerClass={styles.field}
              //   placeholder={t('placeholders.phone')}
              //   label={t('fields.phone')}
              //   viewStyle="secondary"
              //   showClearButton
              //   error={!!errors?.phone?.message}
              //   errorMessage={errors?.phone?.message && t('errors.' + errors.phone.message)}
              //   autoComplete="phone"
              //   {...rest}
              //   {...register}
              //   value={value}
              // />
            )}
          />
        </form>
        <FileDropzone
          placeholder={<Trans i18nKey="send_to_counteragent_sign_files" />}
          accept={{
            'image/jpeg': [], 'image/png': [], 'image/bmp': [], 'image/gif': [], 'image/tiff': [], 'image/webp': []
          }}
          previews={{ columns: 3 }}
          maxFiles={10}
          className={styles.dropzone}
          onChange={setFiles}
        />
      </div>
    </Modal>
  );
};

export default SendSignModal;
